import {
  AUTH_LOGIN,
  AUTH_LOGIN_WITH_SESSION_TOKEN,
  ERROR_FAILED_AUTHENTICATE_USER,
  ERROR_INVALID_CREDENTIALS,
  ERROR_MISSING_FLOW_ID,
  ERROR_NO_SESSION_TOKEN
} from '@/features/login/constants';
import { getUser, getUserId } from '@/features/login/selectors';
import {
  ADD_DEVICE,
  CHECK_VERIFY_STATUS,
  ERROR_NO_AUTHORIZED_USER_FOUND,
  FETCH_DEVICES,
  MFA_SIGNIN,
  REMOVE_DEVICE,
  RESET_QUOTA,
  VERIFY_OTP
} from '@/features/mfa/constants';
import { cookieService } from '@/services/cookieService';
import sportsAuthorityService from '@/services/sportsAuthorityService';

import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { propOr } from 'ramda';

const sessionToken = cookieService.getCookie('session_token');
const venueIds = cookieService.getCookie('venue_ids');
const orgId = cookieService.getCookie('org_id');

export const logout = createAsyncThunk('auth/logout', async (_, { dispatch }) => {
  dispatch(resetAuthState());
});

export const fetchDevices = createAsyncThunk(
  FETCH_DEVICES,
  async (_, { getState, rejectWithValue }) => {
    const sessionToken = getState().auth.user?.session_token;
    if (!sessionToken) {
      console.error('Session token is missing for fetchDevices.');
      return rejectWithValue({ error: ERROR_NO_SESSION_TOKEN });
    }
    try {
      const response = await sportsAuthorityService.fetchMfaDevices(sessionToken);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch devices:', error);
      return rejectWithValue({ error: 'Failed to fetch devices.' });
    }
  }
);

export const signInWithCredentials = createAsyncThunk(
  AUTH_LOGIN,
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await sportsAuthorityService.authenticateUser(email, password);
      if (!response) {
        throw new Error(ERROR_INVALID_CREDENTIALS);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: ERROR_FAILED_AUTHENTICATE_USER });
    }
  }
);

export const signInWithSessionToken = createAsyncThunk(
  AUTH_LOGIN_WITH_SESSION_TOKEN,
  async (_, { getState, dispatch, rejectWithValue }) => {
    const token = getState().auth.user?.session_token || null;
    if (!token) {
      console.error('Session token is missing.');
      return rejectWithValue({ error: ERROR_NO_SESSION_TOKEN });
    }

    try {
      const response = await sportsAuthorityService.fetchSession(token);
      const account = response.data?.account;
      if (!account) {
        console.error('Account data is missing in the response.');
        return rejectWithValue({ error: ERROR_FAILED_AUTHENTICATE_USER });
      }
      return account;
    } catch (error) {
      console.error('Failed to fetch session:', error);
      dispatch({ type: 'auth/logout' });
      return rejectWithValue(error.response?.data || { error: ERROR_FAILED_AUTHENTICATE_USER });
    }
  }
);

export const mfaSignIn = createAsyncThunk(
  MFA_SIGNIN,
  async (user, { getState, rejectWithValue }) => {
    const state = getState();
    const authorizedUser = user || getUser(state);
    if (!authorizedUser) {
      return rejectWithValue({ error: ERROR_NO_AUTHORIZED_USER_FOUND });
    }
    try {
      const response = await sportsAuthorityService.authorizeMfa(authorizedUser);
      if (response.data.error && response.data.error.code === 'NO_USABLE_DEVICES') {
        return {
          mfaStatus: 'NO_USABLE_DEVICES',
          verifyStatus: 'NOT_INITIATED',
          flowId: response.data.flowId || null,
          devices: [],
          error: response.data.error
        };
      }
      return {
        mfaStatus: response.data.mfaStatus,
        verifyStatus: response.data.verifyStatus,
        flowId: response.data.flowId,
        devices: response.data.devices
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: ERR });
    }
  }
);

export const addDevice = createAsyncThunk(
  ADD_DEVICE,
  async (device, { getState, dispatch, rejectWithValue }) => {
    const state = getState();
    const authorizedUser = state.auth.user;
    try {
      const response = await sportsAuthorityService.addMfaDevice(device, authorizedUser);

      dispatch(fetchDevices());

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to add device.' });
    }
  }
);

export const removeDevice = createAsyncThunk(
  REMOVE_DEVICE,
  async ({ id }, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const user = state.auth.user;
    if (!id) {
      return rejectWithValue({ error: 'Device ID is required to remove a device.' });
    }
    try {
      await sportsAuthorityService.removeMfaDevice(user.id, id);
      dispatch(fetchDevices());
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to remove device.' });
    }
  }
);

export const verifyOtp = createAsyncThunk(
  VERIFY_OTP,
  async ({ otpCode, flowId }, { getState, rejectWithValue }) => {
    const state = getState();
    // const flowId = getFlowId(state);
    const accountId = getUserId(state);

    if (!flowId || !accountId) {
      return rejectWithValue(ERROR_MISSING_FLOW_ID);
    }

    try {
      const response = await sportsAuthorityService.verifyOtpCode(otpCode, flowId, accountId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const checkVerifyStatus = createAsyncThunk(
  CHECK_VERIFY_STATUS,
  async (_, { getState, rejectWithValue }) => {
    const sessionToken = getState().auth.user?.session_token;
    if (!sessionToken) {
      return rejectWithValue({ error: 'Session token missing for verify status check.' });
    }
    try {
      const response = await sportsAuthorityService.checkMfaVerifyStatus(sessionToken);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to check verify status.' });
    }
  }
);

export const resetMfaQuota = createAsyncThunk(
  RESET_QUOTA,
  async (_, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const authorizedUser = state.auth.user;
    const email = authorizedUser?.email;

    if (!authorizedUser) {
      return rejectWithValue({ error: ERROR_NO_AUTHORIZED_USER_FOUND });
    }

    try {
      const response = await sportsAuthorityService.resetMfaQuota(email);
      dispatch(mfaSignIn());
      return response.data;
    } catch (error) {
      console.error('Failed to reset MFA quota, error:', error);
      return rejectWithValue(error.response?.data || { error: 'Failed to reset MFA quota.' });
    }
  }
);

// Basic selectors
export const getAuth = (state) => state.auth;
export const getProfile = createSelector(getAuth, (auth) => propOr({}, 'user', auth));

// Hydration: merge cookies into user if available
export const hydratedUser = sessionToken
  ? {
      session_token: sessionToken,
      organization_id: orgId,
      venue_ids: venueIds,
      mfa_status: 'INIT'
    }
  : {};

export const initialState = {
  user: hydratedUser,
  isLoading: false,
  isSignInLoading: false,
  mfaLoading: false,
  skipped: false,
  hasFetchedDevices: false,
  hasFetchedSession: false,
  devicesChecked: false,
  error: undefined
};

const loggedOutState = {
  user: {},
  isLoading: false,
  isSignInLoading: false,
  mfaLoading: false,
  skipped: false,
  hasFetchedDevices: false,
  hasFetchedSession: false,
  devicesChecked: false,
  error: undefined
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetAuthState: (state) => {
      Object.assign(state, loggedOutState); // Properly reset state
    },
    setSkipped: (state) => {
      state.skipped = true;
    },
    resetSkipped: (state) => {
      state.skipped = false;
      state.hasFetchedDevices = false;
    },
    updateFlowId: (state, action) => {
      state.user.flow_id = action.payload;
    },
    setUser: (state, action) => {
      state.user = action.payload;
    },
    changePassword: (state, action) => {
      state.newPassword = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Authentication
      .addCase(signInWithCredentials.pending, (state) => {
        state.isSignInLoading = true;
        state.error = undefined;
      })
      .addCase(signInWithCredentials.fulfilled, (state, action) => {
        state.isSignInLoading = false;
        state.user = action.payload;
        state.error = undefined;
        state.hasFetchedDevices = false;
        state.hasFetchedSession = true;
      })
      .addCase(signInWithCredentials.rejected, (state, action) => {
        state.isSignInLoading = false;
        state.error = action.payload.error || action.error.message;
        state.hasFetchedSession = true;
      })
      .addCase(signInWithSessionToken.pending, (state) => {
        state.isSignInLoading = true;
        state.error = undefined;
      })
      .addCase(signInWithSessionToken.fulfilled, (state, action) => {
        if (action.payload?.error) {
          state.error = action.payload.error;
          // Preserve MFA error in a separate field so it's not cleared later.
          state.user.mfa_status = action.payload.mfa_status;
        } else {
          state.user = action.payload;
          state.error = undefined;
        }
        state.isSignInLoading = false;
        state.hasFetchedDevices = true;
        state.hasFetchedSession = true;
      })
      .addCase(signInWithSessionToken.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
        state.hasFetchedSession = true;
        Object.assign(state, loggedOutState); // Properly reset state
        state.user = {};
      })
      // Device Operations
      .addCase(fetchDevices.pending, (state) => {
        state.isLoading = true;
        state.devicesChecked = false;
      })
      .addCase(fetchDevices.fulfilled, (state, action) => {
        if (action.payload?.error) {
          state.error = action.payload.error;
        } else {
          state.user.mfa_devices = action.payload;
          state.error = undefined;
        }
        state.isLoading = false;
        state.devicesChecked = true;
        state.hasFetchedDevices = true;
      })
      .addCase(fetchDevices.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.error || action.payload || 'Unknown error';
        state.devicesChecked = false;
      })
      // MFA Operations
      .addCase(mfaSignIn.pending, (state) => {
        state.mfaLoading = true;
      })
      .addCase(mfaSignIn.fulfilled, (state, action) => {
        state.mfaLoading = false;
        // If MFA sign-in returns a NO_USABLE_DEVICES error, store that error in mfaError
        if (action.payload.mfaStatus === 'NO_USABLE_DEVICES') {
          state.user.mfa_status = 'NO_USABLE_DEVICES';
          state.user.mfa_devices = [];
          state.mfaError = action.payload.error; // New field to store MFA-specific error
          state.devicesChecked = true;
        } else {
          state.user.flow_id = action.payload.flowId;
          state.user.mfa_status = action.payload.mfaStatus;
          state.user.verify_status = action.payload.verifyStatus;
          state.user.mfa_devices = action.payload.devices;
          state.mfaError = undefined;
          state.error = undefined;
          state.devicesChecked = true;
        }
      })
      .addCase(mfaSignIn.rejected, (state, action) => {
        state.mfaLoading = false;
        state.error = action.payload?.error || 'Failed to authorize MFA.';
      })
      .addCase(addDevice.fulfilled, (state, action) => {
        state.user.flow_id = action.payload.flow_id;
        state.user.mfa_status = action.payload.mfaStatus || 'OTP_REQUIRED';
        state.user.verify_status = action.payload.verifyStatus;
        state.user.mfa_devices = action.payload.devices;
        state.isLoading = false;
      })
      .addCase(removeDevice.pending, (state, action) => {
        const deviceId = action.meta.arg.id;
        state.user.mfa_devices = state.user.mfa_devices.filter((device) => device.id !== deviceId);
        state.isLoading = true;
      })
      .addCase(removeDevice.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(removeDevice.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(verifyOtp.pending, (state) => {
        state.mfaLoading = true;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.mfaLoading = false;
        state.user.mfa_status = action.payload?.status;
        state.user.session_token = action.payload?.session_token;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.mfaLoading = false;
        state.error = action.payload?.error || 'Failed to verify OTP';
      })
      // Verify Status
      .addCase(checkVerifyStatus.fulfilled, (state, action) => {
        state.user.verify_status = action.payload.verifyStatus;
      })
      .addCase(checkVerifyStatus.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(resetMfaQuota.fulfilled, (state, action) => {
        state.user.mfa_quota = action.payload.mfa_quota;
        state.user.error = undefined;
        state.mfaError = undefined;
        state.mfaLoading = false;
      })
      .addCase(resetMfaQuota.pending, (state) => {
        state.mfaLoading = true;
      })
      .addCase(resetMfaQuota.rejected, (state, action) => {
        state.error = action.payload?.error || 'Failed to reset MFA quota';
        state.mfaLoading = false;
      })
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.error || 'Failed to log out';
      })
      .addCase(logout.fulfilled, (state) => {
        Object.assign(state, loggedOutState); // Properly reset state
        state.user = {};
      });
  }
});

export const isSignInWithSessionTokenError = (state) => state.auth.error === ERROR_NO_SESSION_TOKEN;

export default authSlice.reducer;
export const { changePassword, resetAuthState, resetSkipped, setSkipped, updateFlowId, setUser } =
  authSlice.actions;
export const { selectors } = { getAuth, getProfile };
